import { elements } from '@ecosystem/newron-design-system';
import React, { CSSProperties, ReactNode, useContext, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ConfigItemsTrayContext } from '../../config-items/components/ConfigItemsTrayContext';
import { useOnOutsideClick } from '../../hooks/useOnOutsideClick';
import { ContextSelectorWidth } from '../types';

export type EntryProps = {
  isLoading: boolean;
  children: ReactNode;
  expanded: boolean;
  onClick: (value: boolean) => void;
  labels?: string;
  isSecondary?: boolean;
  isFirstRender?: boolean;
  width?: ContextSelectorWidth;
  position?: string;
};

export function ContextEntry({
  isLoading,
  labels,
  isSecondary,
  children,
  expanded,
  onClick,
  isFirstRender,
  width = ContextSelectorWidth.max,
  position = 'left',
}: EntryProps) {
  const el = useRef<HTMLDivElement>(null);
  const context = useContext(ConfigItemsTrayContext);
  const portalRef = useRef<HTMLDivElement>(null);

  const { Icon } = elements;
  const [hovered, setHovered] = useState(false);

  useOnOutsideClick(el, event => {
    // If the click is outside the element, close the element
    // Or if the element is rendered in portal & click is outside the portal, then close the element
    if (!portalRef.current?.contains(event.target as Node)) {
      onClick(false);
    }
  });

  React.useEffect(() => {
    // If the context is used for multi edit page, create a portal div and append it to the body
    // This is used to render the children in a portal below the parent element
    // This is to avoid the issue of the children being cut off by the parent element in multi edit page
    if (context?.isMultiEditPage) {
      const portalDiv = document.createElement('div');
      document.body.appendChild(portalDiv);
      portalRef.current = portalDiv;
      return () => {
        document.body.removeChild(portalDiv);
      };
    }
  }, [context]);

  const renderChildren = React.useCallback(() => {
    // If context is not used for multi edit page or parent not rendered yet, just return default children.
    if (!context?.isMultiEditPage || !el.current) {
      return children;
    }

    // Else, render the children in a portal just below the parent element.
    const { bottom: parentBottom, left: parentLeft } = el.current.getBoundingClientRect();
    const { offsetWidth: parentWidth } = el.current;
    const portalStyle: CSSProperties = {
      position: 'absolute',
      top: parentBottom + window.scrollY,
      left: position === 'left' ? parentLeft + parentWidth + window.scrollX : parentLeft + window.scrollX,
    };

    return ReactDOM.createPortal(<div style={portalStyle}>{children}</div>, portalRef.current);
  }, [children, context, position, el]);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onMouseOut={() => expanded && setHovered(false)}
      onBlur={() => setHovered(false)}
      className={`relative ${width}`}
      ref={isFirstRender ? null : el}
      data-test-id="context-entry"
    >
      <button
        disabled={isLoading}
        onClick={() => onClick(!expanded)}
        className={`flex rounded pt-1 pb-1 pl-2 text-left ${hovered && !expanded && 'bg-brand-light'} ${
          expanded && 'bg-medium text-brand'
        } ${hovered ? 'text-brand' : 'text-default'}`}
        data-test-id="multicell-selector-btn"
      >
        <div className={` ${isSecondary ? 'font-normal' : 'font-bold'}`}>
          <div className="flex justify-between items-center">
            <span>{labels}</span>
            <Icon hoverColor="brand" color={expanded || hovered ? 'brand' : 'secondaryInteractive'}>
              <Icon.icons.ChevronDown />
            </Icon>
          </div>
        </div>
      </button>
      {expanded && renderChildren()}
    </div>
  );
}
